import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Divider, Tooltip, Paper, Dialog, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { useLanguage } from '../contexts/LanguageContext';
import countersData from '../data/counters.json';

const CounterDisplay = ({ counterPicks, heroes, selectedEnemies, selectedAllies = [], selectedMap, selectedRank }) => {
  const { t } = useLanguage();
  const [selectedHero, setSelectedHero] = useState(null);
  const [alliedHeroScores, setAlliedHeroScores] = useState([]);

  const getHeroByName = (name) => {
    return heroes.find(hero => hero.name === name) || null;
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'VANGUARD': return '#3498db';
      case 'DUELIST': return '#e74c3c';
      case 'STRATEGIST': return '#2ecc71';
      default: return '#fbdb2b';
    };
  };

  const getScoreColor = (score) => {
    if (score >= 4) return '#4caf50';
    if (score >= 3) return '#8bc34a';
    if (score >= 2) return '#ffc107';
    return '#ff9800';
  };



  // Combine all counter picks into a single array with role information
  const getAllCounterPicks = () => {
    const allPicks = [];
    
    // Add vanguard counters
    if (counterPicks.vanguard && counterPicks.vanguard.length > 0) {
      counterPicks.vanguard.forEach(counter => {
        const hero = getHeroByName(counter.name);
        if (hero) {
          allPicks.push({
            ...counter,
            hero,
            roleColor: '#3498db',
            role: t('vanguard')
          });
        }
      });
    }
    
    // Add duelist counters
    if (counterPicks.duelist && counterPicks.duelist.length > 0) {
      counterPicks.duelist.forEach(counter => {
        const hero = getHeroByName(counter.name);
        if (hero) {
          allPicks.push({
            ...counter,
            hero,
            roleColor: '#e74c3c',
            role: t('duelist')
          });
        }
      });
    }
    
    // Add strategist counters
    if (counterPicks.strategist && counterPicks.strategist.length > 0) {
      counterPicks.strategist.forEach(counter => {
        const hero = getHeroByName(counter.name);
        if (hero) {
          allPicks.push({
            ...counter,
            hero,
            roleColor: '#2ecc71',
            role: t('strategist')
          });
        }
      });
    }
    
    return allPicks;
  };

  // Function to get meta rank label based on rank value
  const getMetaRankLabel = (heroName) => {
    let rankValue = 0;
    
    if (selectedRank) {
      rankValue = countersData[heroName]?.metaRanks?.[selectedRank] || 0;
    } else {
      const diamondPlusValue = countersData[heroName]?.metaRanks?.diamond_plus || 0;
      const belowDiamondValue = countersData[heroName]?.metaRanks?.below_diamond || 0;
      rankValue = (diamondPlusValue + belowDiamondValue) / 2;
    }
    
    if (rankValue >= 1.375) return 'S+';  
    if (rankValue >= 1.125) return 'S';        
    if (rankValue >= 0.75) return 'A';          
    if (rankValue >= 0.375) return 'B';         
    if (rankValue >= 0.25) return 'C';
    return 'New';       
  };
  
  const getMetaRankColor = (rankLabel) => {
    switch(rankLabel) {
      case 'S+': return '#ff5722';
      case 'S': return '#f44336';
      case 'A': return '#ffc107';
      case 'B': return '#4caf50';
      case 'C': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  // Function to generate tooltip content
  const generateTooltipContent = (counter) => {
    const heroName = counter.hero.name;
    const metaRankLabel = getMetaRankLabel(heroName);
    const metaRankColor = getMetaRankColor(metaRankLabel);
    
    // Check for map bonus
    const hasMapBonus = selectedMap && countersData[heroName]?.bestMaps?.includes(selectedMap);
    
    // Check for synergies with other recommended heroes and allied heroes
    const synergies = countersData[heroName]?.synergies || [];
    const allRecommendedHeroes = counterPicks ? getAllCounterPicks().map(pick => pick.hero.name) : [];
    const allAlliedHeroes = selectedAllies.map(hero => hero.name);
    const synergiesWith = synergies.filter(synergyHero => 
      allRecommendedHeroes.includes(synergyHero) || allAlliedHeroes.includes(synergyHero)
    );
    
    // Only calculate enemy counters if we have selected enemies
    const hasEnemyData = selectedEnemies && selectedEnemies.length > 0;
    
    // Find which enemies this hero counters (only if we have enemy data)
    const countersEnemies = hasEnemyData ? selectedEnemies.filter(enemy => {
      if (!countersData[enemy.name]) return false;
      return (
        countersData[enemy.name].vanguardCounters.includes(heroName) ||
        countersData[enemy.name].duelistCounters.includes(heroName) ||
        countersData[enemy.name].strategistCounters.includes(heroName)
      );
    }) : [];
    
    // Find which enemies counter this hero (only if we have enemy data)
    const counteredBy = hasEnemyData ? selectedEnemies.filter(enemy => {
      if (!countersData[heroName]) return false;
      return (
        countersData[heroName].vanguardCounters.includes(enemy.name) ||
        countersData[heroName].duelistCounters.includes(enemy.name) ||
        countersData[heroName].strategistCounters.includes(enemy.name)
      );
    }) : [];
    
    return (
      <Paper sx={{ p: 1.5, maxWidth: 300, backgroundColor: '#2b3340', border: '1px solid rgba(255,255,255,0.2)' }}>
        <Typography variant="subtitle2" sx={{ color: '#fbdb2b', fontWeight: 'bold', mb: 1 }}>
          {heroName}
        </Typography>
        
        {/* Meta Rank with enhanced styling */}
        <Box sx={{ display: 'flex', mb: 0.5, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium', minWidth: 120 }}>
            {t('metaRank')}:
          </Typography>
          <Box
            onClick={() => handleHeroClick(counter)}
            sx={{
              backgroundColor: metaRankColor,
              color: 'white',
              fontWeight: 'bold',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '0.875rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '28px'
            }}
          >
            {metaRankLabel}
          </Box>
        </Box>
        
        {/* Map Bonus */}
        {selectedMap && (
          <Box sx={{ display: 'flex', mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium', minWidth: 120 }}>
              {t('mapBonus')}:
            </Typography>
            <Typography variant="body2" sx={{ color: hasMapBonus ? '#4caf50' : 'white' }}>
              {hasMapBonus ? t('yes') : t('no')}
            </Typography>
          </Box>
        )}
        
        {/* Only show enemy counter information if we have enemy data */}
        {hasEnemyData && countersEnemies.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium', mb: 0.5 }}>
              {t('countersEnemies')}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
              {countersEnemies.map((enemy, index) => (
                <React.Fragment key={enemy.id}>
                  <Typography variant="body2" sx={{ color: '#4caf50' }}>
                    {enemy.name}
                  </Typography>
                  {index < countersEnemies.length - 1 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                      •
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
        
        {/* Countered By with separators - only show if we have enemy data */}
        {hasEnemyData && counteredBy.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium', mb: 0.5 }}>
              {t('counteredBy')}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
              {counteredBy.map((enemy, index) => (
                <React.Fragment key={enemy.id}>
                  <Typography variant="body2" sx={{ color: '#f44336' }}>
                    {enemy.name}
                  </Typography>
                  {index < counteredBy.length - 1 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                      •
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
        
        {/* Synergies with separators */}
        {synergiesWith.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium', mb: 0.5 }}>
              {t('synergiesWith')}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
              {synergiesWith.map((synergyHero, index) => (
                <React.Fragment key={index}>
                  <Typography variant="body2" sx={{ color: '#2196f3' }}>
                    {synergyHero}
                  </Typography>
                  {index < synergiesWith.length - 1 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                      •
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  const handleHeroClick = (counter) => {
    setSelectedHero(counter);
  };

  const handleCloseDialog = () => {
    setSelectedHero(null);
  };

  const renderCounterCard = (counter, index) => {
    return (
      <Grid item xs={6} sm={4} md={4} key={index}>
        <Tooltip
          title={generateTooltipContent(counter)}
          placement="right"
          arrow
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: 'transparent',
                p: 0
              },
              '& .MuiTooltip-arrow': {
                color: '#2b3340'
              }
            }
          }}
        >
          <Box
            onClick={() => handleHeroClick(counter)}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 2,
              overflow: 'hidden',
              border: `2px solid ${counter.roleColor}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: `0 5px 15px rgba(${counter.roleColor.replace('#', '')}, 0.3)`
              }
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <img
                src={counter.hero.image}
                alt={counter.hero.name}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  right: 0, 
                  backgroundColor: getScoreColor(counter.score),
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  padding: '2px 6px',
                  borderBottomLeftRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <StarIcon sx={{ fontSize: '0.8rem' }} />
                {counter.score}
              </Box>
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  backgroundColor: counter.roleColor,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.6rem',
                  padding: '2px 6px',
                  borderBottomRightRadius: '8px',
                }}
              >
                {t(counter.hero.role.toLowerCase())}
              </Box>
            </Box>
            <Typography
              variant="body2"
              align="center"
              sx={{ 
                display: 'block', 
                py: 1,
                color: 'white',
                fontWeight: 'medium'
              }}
            >
              {counter.hero.name}
            </Typography>
          </Box>
        </Tooltip>
      </Grid>
    );
  };

  // Calculate scores for allied heroes
  useEffect(() => {
    if (selectedAllies.length === 0) {
      setAlliedHeroScores([]);
      return;
    }

    const scores = selectedAllies.map(ally => {
      let score = 0;
      
      // Calculate enemy-based scores if we have enemy data
      if (selectedEnemies && selectedEnemies.length > 0) {
        // Check if this hero counters any of the enemy heroes
        selectedEnemies.forEach(enemy => {
          if (countersData[enemy.name]) {
            if (countersData[enemy.name].vanguardCounters.includes(ally.name) ||
                countersData[enemy.name].duelistCounters.includes(ally.name) ||
                countersData[enemy.name].strategistCounters.includes(ally.name)) {
              score++;
            }
            
            // Subtract if this hero is countered by the enemy
            if (countersData[ally.name]) {
              if (countersData[ally.name].vanguardCounters.includes(enemy.name) ||
                  countersData[ally.name].duelistCounters.includes(enemy.name) ||
                  countersData[ally.name].strategistCounters.includes(enemy.name)) {
                score--;
              }
            }
          }
        });
      } else {
        // When no enemies are selected, add a base score of 1
        // This ensures allied heroes always have a minimum score for display
        score = 1;
      }
      
      // Add bonus for map - always calculate this regardless of enemies
      if (selectedMap && countersData[ally.name]?.bestMaps?.includes(selectedMap)) {
        score++;
      }
      
      // Add bonus for rank performance - always calculate this regardless of enemies
      if (selectedRank) {
        const rankValue = countersData[ally.name]?.metaRanks?.[selectedRank] || 0;
        score += rankValue;
      } else {
        const diamondPlusValue = countersData[ally.name]?.metaRanks?.diamond_plus || 0;
        const belowDiamondValue = countersData[ally.name]?.metaRanks?.below_diamond || 0;
        const averageRankValue = (diamondPlusValue + belowDiamondValue) / 2;
        score += averageRankValue;
      }
      
      // Check for synergies with other allied heroes - always calculate this regardless of enemies
      const synergies = countersData[ally.name]?.synergies || [];
      const otherAlliedHeroes = selectedAllies.filter(a => a.name !== ally.name).map(a => a.name);
      const hasSynergyWithAllies = synergies.some(synergyHero => otherAlliedHeroes.includes(synergyHero));
      
      if (hasSynergyWithAllies) {
        score++;
      }
      
      // We no longer count synergies with recommended heroes for allied heroes
      // This is intentional - only recommended heroes get synergy bonuses with allied heroes
      // Allied heroes do not get synergy bonuses with recommended heroes
      
      return {
        hero: ally,
        score,
        roleColor: getRoleColor(ally.role),
        role: ally.role
      };
    });
    
    setAlliedHeroScores(scores);
  }, [selectedAllies, selectedEnemies, selectedMap, selectedRank, counterPicks]);

  return (
    <Box>
      {
        <Box>
          
          {selectedAllies.length > 0 && (
              <Grid container spacing={2}>
                {alliedHeroScores.map((ally, index) => renderCounterCard(ally, index))}
              </Grid>
          )}
          <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
        </Box>
      }

      {/* Only show counter picks section when enemies are selected */}
      {(selectedEnemies.length > 0 || selectedAllies.length > 0) && (
        <>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              position: 'relative',
              paddingBottom: 1,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '40px',
                height: '3px',
                backgroundColor: '#fbdb2b',
                borderRadius: '2px'
              }
            }}
          >
            {t('recommendedCounterPicks')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {getAllCounterPicks().map((counter, index) => renderCounterCard(counter, index))}
            </Grid>
          </Box>

          {counterPicks.tiedPicks && counterPicks.tiedPicks.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'relative',
                  paddingBottom: 1,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '30px',
                    height: '2px',
                    backgroundColor: '#fbdb2b',
                    borderRadius: '2px'
                  }
                }}
              >
                {t('alternativePicks')}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {counterPicks.tiedPicks.map((counter, index) => {
                  const hero = getHeroByName(counter.name);
                  if (!hero) return null;
                  
                  const roleColor = getRoleColor(hero.role);
                  const counterWithHero = { ...counter, hero, roleColor, role: t(hero.role.toLowerCase()) };

                  return renderCounterCard(counterWithHero, index);
                })}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Hero Details Dialog */}
      <Dialog
        open={selectedHero !== null}
        onClose={handleCloseDialog}
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#2b3340',
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.2)',
            margin: { xs: 2, sm: 4 }
          }
        }}
      >
        {selectedHero && (
          <>
            <Box sx={{ position: 'relative', p: 2 }}>
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'rgba(255,255,255,0.7)'
                }}
              >
                <CloseIcon />
              </IconButton>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `2px solid ${selectedHero.roleColor}`
                  }}
                >
                  <img
                    src={selectedHero.hero.image}
                    alt={selectedHero.hero.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                    {selectedHero.hero.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      label={selectedHero.role}
                      sx={{
                        backgroundColor: selectedHero.roleColor,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    <Chip
                      icon={<StarIcon />}
                      label={selectedHero.score}
                      sx={{
                        backgroundColor: getScoreColor(selectedHero.score),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            
            <Box sx={{ p: 2 }}>
              {generateTooltipContent(selectedHero)}
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CounterDisplay;